import { MediaOutputSpec } from './media-spec';
export type MediaType = 'image' | 'ai_video' | 'audio';


export interface MediaItem {
  id: string;
  type: MediaType;
  prompt: string;
  file_url: string;
  metadata: Record<string, any>;
  output_spec?: MediaOutputSpec;
}

// ==================== Narration measurement ====================

/**
 * What the backend measured on a narration (TTS) take: how long the audio runs,
 * how many words it speaks, and the rate the two imply. Stamped on the media
 * item's metadata once measured — a take made before measuring existed carries
 * none of it, and is simply treated as unknown.
 */
export interface NarrationMeasurement {
  duration_seconds: number;
  words: number;
  words_per_second: number;
}

/** The measurement on a narration take, or null while it is unknown. */
export const readNarrationMeasurement = (
  item?: MediaItem | null
): NarrationMeasurement | null => {
  const metadata = item?.metadata;
  const seconds = Number(metadata?.duration_seconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  const words = Number(metadata?.words);
  const wordsPerSecond = Number(metadata?.words_per_second);
  return {
    duration_seconds: seconds,
    words: Number.isFinite(words) ? words : 0,
    words_per_second: Number.isFinite(wordsPerSecond) ? wordsPerSecond : 0,
  };
};

// ==================== Clip-side reviewer (Clip QA) ====================

/**
 * One re-render candidate for a reviewed scene item, carried in
 * `metadata.review_variants` on the canonical scene `MediaItem`. Clip QA never
 * overwrites the canonical item in place — a re-render lands as a NEW detached
 * media item, recorded here, and `select-variant` swaps which one is canonical.
 * Mirrors the Go backend's clipqa review-variant shape.
 */
export interface ReviewVariant {
  media_id: string;
  file_url: string;
  source: 'original' | 'rerender';
  attempt: number;
  pass: boolean;
  fail_count: number;
  created_at: string;
}

/** Actions the clip-side reviewer appends to `metadata.review_log`. */
export type ReviewLogAction =
  | 'judged'
  | 'trim_applied'
  | 'rerendered'
  | 'variant_selected'
  | 'qa_skipped';

/** One append-only audit entry of `metadata.review_log`, oldest first. */
export interface ReviewLogEntry {
  ts: string;
  action: ReviewLogAction;
  detail: string;
}